import { useRef, useEffect, useState } from 'react';
import { useTheme } from '../context/ThemeContext';

export default function ProgressCard({ measurements, user }) {
  const canvasRef = useRef(null);
  const { theme } = useTheme();
  const [ready, setReady] = useState(false);

  const sorted = [...(measurements||[])].sort((a,b)=>new Date(a.measured_at)-new Date(b.measured_at));
  const first  = sorted[0];
  const last   = sorted[sorted.length-1];

  const days = first && last
    ? Math.round((new Date(last.measured_at)-new Date(first.measured_at))/(24*3600*1000))
    : 0;

  const delta = (field) => {
    if (!first?.[field] || !last?.[field]) return null;
    return Math.round((parseFloat(last[field])-parseFloat(first[field]))*10)/10;
  };

  const METRICS = [
    { field:'weight_kg',  label:'Вес',   unit:'кг', goodDown:true },
    { field:'waist_cm',   label:'Талия', unit:'см', goodDown:true },
    { field:'abdomen_cm', label:'Живот', unit:'см', goodDown:true },
    { field:'hips_cm',    label:'Бёдра', unit:'см', goodDown:true },
  ].filter(m => delta(m.field) !== null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !first || !last) return;
    const ctx = canvas.getContext('2d');
    const W = 1080, H = 1080;
    canvas.width = W; canvas.height = H;

    // Background gradient
    const bg = ctx.createLinearGradient(0, 0, W, H);
    bg.addColorStop(0, '#0D0B1A');
    bg.addColorStop(0.5, '#1A0830');
    bg.addColorStop(1, '#0A0A1C');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    // Decorative circles
    const drawOrb = (x, y, r, color) => {
      const g = ctx.createRadialGradient(x,y,0,x,y,r);
      g.addColorStop(0, color);
      g.addColorStop(1, 'transparent');
      ctx.fillStyle = g;
      ctx.beginPath(); ctx.arc(x,y,r,0,Math.PI*2); ctx.fill();
    };
    drawOrb(200, 200, 300, `${theme.accent}40`);
    drawOrb(880, 880, 280, 'rgba(155,111,212,0.25)');
    drawOrb(900, 150, 180, 'rgba(0,207,219,0.15)');

    // Grid lines
    ctx.strokeStyle = 'rgba(232,0,111,0.06)';
    ctx.lineWidth = 1;
    for (let x=0; x<W; x+=60) { ctx.beginPath(); ctx.moveTo(x,0); ctx.lineTo(x,H); ctx.stroke(); }
    for (let y=0; y<H; y+=60) { ctx.beginPath(); ctx.moveTo(0,y); ctx.lineTo(W,y); ctx.stroke(); }

    // Top accent line
    const accent = ctx.createLinearGradient(0,0,W,0);
    accent.addColorStop(0, 'transparent');
    accent.addColorStop(0.3, theme.accent);
    accent.addColorStop(0.7, theme.accent);
    accent.addColorStop(1, 'transparent');
    ctx.fillStyle = accent;
    ctx.fillRect(0, 0, W, 4);

    // Logo MS
    ctx.save();
    const logoGrad = ctx.createLinearGradient(60,60,130,130);
    logoGrad.addColorStop(0, theme.accent);
    logoGrad.addColorStop(1, '#8B003D');
    ctx.fillStyle = logoGrad;
    roundRect(ctx, 60, 60, 80, 80, 18);
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 32px system-ui';
    ctx.textAlign = 'center';
    ctx.fillText('MS', 100, 112);
    ctx.restore();

    // Brand name
    ctx.fillStyle = '#F2EEF9';
    ctx.font = 'bold 36px system-ui';
    ctx.textAlign = 'left';
    ctx.fillText('MolShape', 160, 112);

    // Trainer tag
    ctx.fillStyle = theme.accent;
    ctx.font = '24px system-ui';
    ctx.fillText('@molchanova_fit', 160, 145);

    // Client name
    const clientName = user?.first_name ? `${user.first_name} ${user.last_name||''}`.trim() : '';
    if (clientName) {
      ctx.fillStyle = 'rgba(255,255,255,0.5)';
      ctx.font = '26px system-ui';
      ctx.fillText(clientName, 60, 195);
    }

    // Main headline
    ctx.fillStyle = '#F2EEF9';
    ctx.font = 'bold 72px system-ui';
    ctx.textAlign = 'center';
    ctx.fillText('МОЙ ПРОГРЕСС', W/2, 310);

    // Period
    const fmt = d => new Date(d).toLocaleDateString('ru-RU',{day:'numeric',month:'long',year:'numeric'});
    ctx.fillStyle = 'rgba(255,255,255,0.45)';
    ctx.font = '28px system-ui';
    ctx.fillText(`${fmt(first.measured_at)}  →  ${fmt(last.measured_at)}`, W/2, 360);

    // Days badge
    ctx.save();
    const badgeGrad = ctx.createLinearGradient(W/2-80,380,W/2+80,430);
    badgeGrad.addColorStop(0, theme.accent);
    badgeGrad.addColorStop(1, '#9B6FD4');
    ctx.fillStyle = badgeGrad;
    roundRect(ctx, W/2-90, 380, 180, 52, 26);
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 26px system-ui';
    ctx.fillText(`${days} дней`, W/2, 415);
    ctx.restore();

    // Metrics cards
    const cols = Math.min(METRICS.length, 4);
    const cardW = 220, cardH = 200, gap = 20;
    const totalW = cols * cardW + (cols-1) * gap;
    const startX = (W - totalW) / 2;
    const startY = 480;

    METRICS.slice(0,4).forEach((m, i) => {
      const d = delta(m.field);
      const x = startX + i * (cardW + gap);
      const y = startY;
      const isGood = m.goodDown ? d<0 : d>0;
      const color = d===0?'#F59E0B':isGood?'#22C55E':'#EF4444';
      const bgAlpha = d===0?'rgba(245,158,11,0.12)':isGood?'rgba(34,197,94,0.12)':'rgba(239,68,68,0.12)';

      // Card bg
      ctx.fillStyle = 'rgba(26,21,37,0.85)';
      roundRect(ctx, x, y, cardW, cardH, 20);
      ctx.fill();
      ctx.strokeStyle = isGood?'rgba(34,197,94,0.3)':'rgba(239,68,68,0.2)';
      ctx.lineWidth = 1.5;
      roundRect(ctx, x, y, cardW, cardH, 20);
      ctx.stroke();

      // Label
      ctx.fillStyle = 'rgba(200,185,220,0.6)';
      ctx.font = 'bold 18px system-ui';
      ctx.textAlign = 'center';
      ctx.fillText(m.label.toUpperCase(), x+cardW/2, y+38);

      // Delta value
      ctx.fillStyle = color;
      ctx.font = `bold ${Math.abs(d)>=10?60:68}px system-ui`;
      const sign = d>0?'+':'';
      ctx.fillText(`${sign}${d}`, x+cardW/2, y+115);

      // Unit
      ctx.fillStyle = 'rgba(200,185,220,0.6)';
      ctx.font = '22px system-ui';
      ctx.fillText(m.unit, x+cardW/2, y+148);

      // Values
      ctx.fillStyle = 'rgba(255,255,255,0.3)';
      ctx.font = '18px system-ui';
      ctx.fillText(`${parseFloat(first[m.field])} → ${parseFloat(last[m.field])}`, x+cardW/2, y+178);
    });

    // Bottom quote
    ctx.fillStyle = 'rgba(255,255,255,0.25)';
    ctx.font = 'italic 26px system-ui';
    ctx.textAlign = 'center';
    ctx.fillText('"Результат — это не случайность, это работа"', W/2, 780);

    // Bottom CTA
    ctx.fillStyle = 'rgba(255,255,255,0.15)';
    ctx.font = '24px system-ui';
    ctx.fillText('molshape.ru', W/2, 840);

    // Bottom accent line
    ctx.fillStyle = accent;
    ctx.fillRect(0, H-4, W, 4);

    setReady(true);
  }, [first, last, theme]);

  const download = () => {
    const canvas = canvasRef.current;
    const link = document.createElement('a');
    link.download = 'molshape_progress.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  if (!first || !last || sorted.length < 2) return null;

  const { isDark } = useTheme() || {};

  return (
    <div style={{ marginTop:24 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
        <p style={{ color:'rgba(200,185,220,.6)', fontSize:'.68rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'.1em', margin:0 }}>
          Карточка для Instagram
        </p>
        {ready && (
          <button onClick={download} style={{
            padding:'.5rem 1.1rem', borderRadius:10, background:theme.accent, border:'none',
            color:'#fff', fontSize:'.8rem', fontWeight:700, cursor:'pointer',
            boxShadow:`0 4px 14px ${theme.glow}`,
          }}>
            ⬇ Скачать PNG
          </button>
        )}
      </div>
      <div style={{ borderRadius:16, overflow:'hidden', boxShadow:'0 8px 32px rgba(0,0,0,.4)' }}>
        <canvas ref={canvasRef} style={{ width:'100%', height:'auto', display:'block' }}/>
      </div>
    </div>
  );
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x+r, y);
  ctx.lineTo(x+w-r, y);
  ctx.quadraticCurveTo(x+w, y, x+w, y+r);
  ctx.lineTo(x+w, y+h-r);
  ctx.quadraticCurveTo(x+w, y+h, x+w-r, y+h);
  ctx.lineTo(x+r, y+h);
  ctx.quadraticCurveTo(x, y+h, x, y+h-r);
  ctx.lineTo(x, y+r);
  ctx.quadraticCurveTo(x, y, x+r, y);
  ctx.closePath();
}
